import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Avatar, Button, Popconfirm, Segmented, Select, Tag, Tooltip, theme } from "antd";
import {
  CalendarOutlined,
  DeleteOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  StopOutlined,
  UserOutlined
} from "@ant-design/icons";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import axios from "axios";
import { useTranslation } from "react-i18next";

import "./tasks.css";
import ShowDrawer from "./ShowDrawer";
import TaskDrawer from "./TaskDrawer";
import { Error, Success } from "../../../commons/Messages/Messages";
import { PRIORITY_TAG, formatTaskDate } from "./Tasks.constants";
import { useIsMobile } from "../../../design-system/useMediaQuery";

// The one task board used by the Tasks page, a ticket's workflow and an employee's profile.
export const TASK_COLUMNS = [
  { key: "Pending", label: "Pending" },
  { key: "In Progress", label: "In Progress" },
  { key: "Completed", label: "Completed" },
  { key: "Finished", label: "Finished" }
];

// Column accents come from the theme so they follow the brand and semantic colors.
const useColumnTones = () => {
  const { token } = theme.useToken();
  return {
    Pending: { color: token.colorPrimary, light: token.colorPrimaryBg },
    "In Progress": { color: token.colorWarning, light: token.colorWarningBg },
    Completed: { color: token.colorSuccess, light: token.colorSuccessBg },
    Finished: { color: token.colorTextTertiary, light: token.colorFillSecondary }
  };
};

const authHeaders = () => ({ Authorization: "Bearer " + localStorage.getItem("token") });
const api = path => `${process.env.REACT_APP_BACKEND_URL}${path}`;

export const groupByStatus = tasks =>
  Object.fromEntries(
    TASK_COLUMNS.map(col => [
      col.key,
      tasks
        .filter(t => (t?.type || "Pending") === col.key)
        .sort((a, b) => (a?.sequence ?? 0) - (b?.sequence ?? 0))
    ])
  );

const formatTime = totalSeconds => {
  if (!totalSeconds) return "0s";
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = Math.floor(totalSeconds % 60);
  return `${h > 0 ? h + "h " : ""}${m > 0 ? m + "m " : ""}${s}s`;
};

const LIVE_TAG = { running: "success", paused: "warning", checking: "processing" };

const fullName = person => [person?.firstName, person?.lastName].filter(Boolean).join(" ");

// Sizes the board to the space left in its scroll container, so the columns scroll instead of
// the page and the columns both scrolling.
const useFitHeight = (ref, enabled) => {
  const [height, setHeight] = useState();
  useLayoutEffect(() => {
    if (!enabled || !ref.current) return undefined;
    const measure = () => {
      const node = ref.current;
      if (!node) return;
      const scroller = node.closest(".ant-drawer-body, .ant-layout-content");
      const top = node.getBoundingClientRect().top;
      if (!scroller) {
        setHeight(Math.max(Math.floor(window.innerHeight - top - 16), 380));
        return;
      }
      // Space the wrappers keep below the board (their bottom padding, border and margin), so
      // the board ends exactly where the visible area does.
      let below = 0;
      for (let el = node; el && el !== scroller; el = el.parentElement) {
        const own = getComputedStyle(el);
        below += parseFloat(own.marginBottom) || 0;
        const parent = getComputedStyle(el.parentElement);
        below +=
          (parseFloat(parent.paddingBottom) || 0) + (parseFloat(parent.borderBottomWidth) || 0);
      }
      const visibleBottom =
        scroller.getBoundingClientRect().top + scroller.clientTop + scroller.clientHeight;
      setHeight(Math.max(Math.floor(visibleBottom - top - below - 1), 380));
    };
    measure();
    window.addEventListener("resize", measure);
    const observer = new ResizeObserver(measure);
    if (ref.current.parentElement) observer.observe(ref.current.parentElement);
    return () => {
      window.removeEventListener("resize", measure);
      observer.disconnect();
    };
  }, [enabled]);
  return height;
};

const TaskTimer = ({ task, onChanged }) => {
  const { t } = useTranslation("dashboard");
  const [localSeconds, setLocalSeconds] = useState(0);
  const [busy, setBusy] = useState(false);
  const status = task.timerStatus || "idle";

  useEffect(() => {
    if (status === "idle" || !task.lastTimerTransition) {
      setLocalSeconds(0);
      return undefined;
    }
    const start = new Date(task.lastTimerTransition).getTime();
    const tick = () => setLocalSeconds(Math.max(Math.floor((Date.now() - start) / 1000), 0));
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [status, task.lastTimerTransition]);

  const act = async action => {
    setBusy(true);
    try {
      await axios.put(
        api(`/tasks/toggle-timer/${task._id}`),
        { action },
        { headers: authHeaders() }
      );
    } catch (err) {
      Error(err.response?.data?.errorMessage || err.message);
    } finally {
      setBusy(false);
      onChanged();
    }
  };

  const isRunning = status === "running";
  const isChecking = status === "checking";
  const times = [
    [t("Work"), (task.workTime || 0) + (isRunning ? localSeconds : 0), isRunning],
    [
      t("Pause"),
      (task.bossTime || 0) + (status === "paused" ? localSeconds : 0),
      status === "paused"
    ],
    [t("Check"), (task.checkerTime || 0) + (isChecking ? localSeconds : 0), isChecking]
  ];

  return (
    <div className="desk-card__timer">
      <div className="desk-card__times">
        {times.map(([label, seconds, active]) => (
          <div key={label} className={"desk-card__time" + (active ? " is-active" : "")}>
            <span>{label}</span>
            <strong>{formatTime(seconds)}</strong>
          </div>
        ))}
      </div>
      {task.type !== "Finished" && (
        <div className="desk-card__timer-actions">
          {!isChecking && (
            <Button
              size="small"
              {...(isRunning ? { color: "orange", variant: "solid" } : { type: "primary" })}
              className="desk-timer-btn"
              loading={busy}
              icon={isRunning ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
              onClick={() => act(isRunning ? "pause" : "start")}
            >
              {isRunning ? t("Pause") : status === "paused" ? t("Resume") : t("Start")}
            </Button>
          )}
          <Button
            size="small"
            className="desk-timer-btn is-ghost"
            disabled={busy}
            onClick={() => act(isChecking ? "stop" : "check")}
          >
            {isChecking ? t("Done") : t("Check")}
          </Button>
          {status !== "idle" && !isChecking && (
            <Tooltip title={t("Stop")}>
              <Button
                size="small"
                danger
                className="desk-timer-btn is-stop"
                disabled={busy}
                icon={<StopOutlined />}
                aria-label={t("Stop")}
                onClick={() => act("stop")}
              />
            </Tooltip>
          )}
        </div>
      )}
    </div>
  );
};

export const TaskCard = ({
  item,
  provided,
  snapshot,
  onDelete,
  onChanged,
  readOnly,
  showTimer,
  onStatusChange
}) => {
  const { t } = useTranslation("dashboard");
  const { token } = theme.useToken();
  const isMobile = useIsMobile();
  const status = item.timerStatus || "idle";

  return (
    <div
      {...(provided?.draggableProps || {})}
      {...(provided?.dragHandleProps || {})}
      ref={provided?.innerRef}
      className={"desk-card" + (snapshot?.isDragging ? " desk-card--dragging" : "")}
    >
      <div className="desk-card__header">
        <Tag color={PRIORITY_TAG[item?.priority] || PRIORITY_TAG.Medium} bordered={false}>
          {t(item?.priority || "Medium")}
        </Tag>
        {showTimer && status !== "idle" ? (
          <Tag color={LIVE_TAG[status] || "warning"} bordered={false}>
            {status === "running"
              ? t("Working")
              : status === "checking"
                ? t("Checking")
                : t("Paused")}
          </Tag>
        ) : (
          <span className="desk-card__date">
            <CalendarOutlined />
            {formatTaskDate(item?.startDate, "DD MMM")}
          </span>
        )}
      </div>

      <p className="desk-card__title" title={item?.title}>
        {item?.title}
      </p>

      {item?.description && showTimer && <p className="desk-card__desc">{item.description}</p>}

      {item?.leader && (
        <div className="desk-card__leader">
          <Avatar
            size={22}
            icon={<UserOutlined />}
            style={{ backgroundColor: token.colorPrimary, flexShrink: 0 }}
          />
          <span className="desk-card__leader-name">{fullName(item.leader)}</span>
        </div>
      )}

      {showTimer && <TaskTimer task={item} onChanged={onChanged} />}

      <div className="desk-card__footer">
        <Avatar.Group
          size={24}
          max={{
            count: 3,
            style: {
              color: token.colorPrimary,
              backgroundColor: token.colorPrimaryBg,
              fontSize: 10,
              border: "none"
            }
          }}
        >
          {item?.team?.map((member, i) => (
            <Tooltip key={member?._id || i} title={fullName(member)}>
              <Avatar
                size={24}
                src={member?.picture?.url}
                icon={<UserOutlined />}
                style={{ border: `2px solid ${token.colorBgContainer}` }}
              />
            </Tooltip>
          ))}
        </Avatar.Group>

        <div className="desk-card__actions">
          {onStatusChange && (
            <Select
              size={isMobile ? "middle" : "small"}
              variant="filled"
              value={item.type || "Pending"}
              onChange={value => onStatusChange(item, value)}
              popupMatchSelectWidth={false}
              className="desk-card__status"
              aria-label={t("Status")}
              options={TASK_COLUMNS.map(col => ({ value: col.key, label: t(col.label) }))}
            />
          )}
          <ShowDrawer item={item} />
          {!readOnly && (
            <>
              <TaskDrawer mode="edit" item={item} updateParent={onChanged} />
              <Popconfirm
                title={t("Are you sure to delete this task?")}
                onConfirm={() => onDelete(item._id)}
                okText={t("Delete")}
                okButtonProps={{ danger: true }}
              >
                <Button
                  type="text"
                  size="small"
                  danger
                  className="desk-card__icon-btn"
                  icon={<DeleteOutlined />}
                  aria-label={t("Delete")}
                />
              </Popconfirm>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * tasks       flat list of tasks
 * onChanged   reload after the server changed something
 * onMoved     optional, called with (task, fromStatus, toStatus) after a move was saved
 * readOnly    hide edit/delete
 * showTimer   work/pause/check timer on each card (ticket workflow)
 * fitHeight   size the board to the visible space so only the columns scroll
 *
 * Phones: the columns sit side by side and snap one per screen; a switcher above jumps to a
 * column and follows the swipe. Cards also get a status picker there, since dragging between
 * off-screen columns is impractical on touch.
 */
const TaskBoard = ({ tasks, onChanged, onMoved, readOnly, showTimer, fitHeight = true }) => {
  const { t } = useTranslation("dashboard");
  const isMobile = useIsMobile();
  const tones = useColumnTones();
  const [columns, setColumns] = useState(() => groupByStatus(tasks));
  const [activeColumn, setActiveColumn] = useState(TASK_COLUMNS[0].key);
  const boardRef = useRef(null);
  const columnRefs = useRef({});
  const height = useFitHeight(boardRef, fitHeight);

  useEffect(() => setColumns(groupByStatus(tasks)), [tasks]);

  // Keep the switcher in step with the column the user swiped to.
  useEffect(() => {
    if (!isMobile || !boardRef.current || typeof IntersectionObserver === "undefined") {
      return undefined;
    }
    const observer = new IntersectionObserver(
      entries => {
        const visible = entries.find(entry => entry.isIntersecting);
        if (visible) setActiveColumn(visible.target.dataset.column);
      },
      { root: boardRef.current, threshold: 0.6 }
    );
    Object.values(columnRefs.current).forEach(node => node && observer.observe(node));
    return () => observer.disconnect();
  }, [isMobile]);

  const jumpToColumn = key => {
    setActiveColumn(key);
    columnRefs.current[key]?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "start"
    });
  };

  const deleteTask = async id => {
    try {
      const res = await axios.delete(api(`/tasks/delete/${id}`), { headers: authHeaders() });
      Success(t(res.data.successMessage));
    } catch (err) {
      Error(err.response?.data?.errorMessage || err.message);
    }
    onChanged();
  };

  // `order` is the destination column in its new order, so the task keeps the spot it was
  // dropped at.
  const saveMove = async (task, to, order) => {
    try {
      await axios.put(
        api("/tasks/sequence"),
        { mainTaskId: task._id, type: to, taskOrder: order.map(x => x._id) },
        { headers: authHeaders() }
      );
      if (task.type !== to) onMoved?.(task, task.type, to);
    } catch (err) {
      Error(err.response?.data?.errorMessage || err.message);
    }
    onChanged();
  };

  const changeStatus = (task, to) => {
    if (to === (task.type || "Pending")) return;
    const order = [...columns[to].filter(x => x._id !== task._id), task];
    setColumns(prev => ({
      ...prev,
      [task.type || "Pending"]: prev[task.type || "Pending"].filter(x => x._id !== task._id),
      [to]: order
    }));
    saveMove(task, to, order);
  };

  const onDragEnd = ({ source, destination, draggableId }) => {
    if (!destination) return;
    const from = source.droppableId;
    const to = destination.droppableId;
    if (from === to && source.index === destination.index) return;
    const task = columns[from].find(x => x._id === draggableId);
    const fromList = [...columns[from]];
    fromList.splice(source.index, 1);
    const toList = from === to ? fromList : [...columns[to]];
    toList.splice(destination.index, 0, { ...task, type: to });
    setColumns(prev => ({ ...prev, [from]: fromList, [to]: toList }));
    saveMove(task, to, toList);
  };

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      {isMobile && (
        <div className="desk-switcher">
          <Segmented
            value={activeColumn}
            onChange={jumpToColumn}
            options={TASK_COLUMNS.map(col => ({
              value: col.key,
              label: (
                <span className="desk-switcher__item">
                  {t(col.label)}
                  <span
                    className="desk-column__badge"
                    style={{ color: tones[col.key].color, background: tones[col.key].light }}
                  >
                    {(columns[col.key] || []).length}
                  </span>
                </span>
              )
            }))}
          />
        </div>
      )}
      <div
        ref={boardRef}
        className={"desk-board" + (height ? " desk-board--fit" : "")}
        style={height ? { height } : undefined}
      >
        {TASK_COLUMNS.map(col => {
          const colTasks = columns[col.key] || [];
          const tone = tones[col.key];
          return (
            <div
              key={col.key}
              className="desk-column"
              data-column={col.key}
              ref={node => {
                columnRefs.current[col.key] = node;
              }}
            >
              <div className="desk-column__head" style={{ borderTopColor: tone.color }}>
                <div className="desk-column__head-left">
                  <span className="desk-column__dot" style={{ background: tone.color }} />
                  <span className="desk-column__name">{t(col.label)}</span>
                </div>
                <span
                  className="desk-column__badge"
                  style={{ color: tone.color, background: tone.light }}
                >
                  {colTasks.length}
                </span>
              </div>

              <Droppable droppableId={col.key}>
                {(provided, snapshot) => (
                  <div
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    className={
                      "desk-column__body" +
                      (snapshot.isDraggingOver ? " desk-column__body--over" : "")
                    }
                  >
                    {colTasks.map((item, index) => (
                      <Draggable key={item._id} draggableId={item._id} index={index}>
                        {(dragProvided, dragSnapshot) => (
                          <TaskCard
                            item={item}
                            provided={dragProvided}
                            snapshot={dragSnapshot}
                            onDelete={deleteTask}
                            onChanged={onChanged}
                            readOnly={readOnly}
                            showTimer={showTimer}
                            onStatusChange={showTimer || isMobile ? changeStatus : undefined}
                          />
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                    {colTasks.length === 0 && !snapshot.isDraggingOver && (
                      <div className="desk-column__empty">{t("Drop tasks here")}</div>
                    )}
                  </div>
                )}
              </Droppable>
            </div>
          );
        })}
      </div>
    </DragDropContext>
  );
};

export default TaskBoard;
